import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './testimonials.css';
import logoJavaHuset from '../../assets/java_huset_logo.svg';
import logoSkeppsholms from '../../assets/skeppsholms.png';
import logoParamPara from '../../assets/parampara_testimonial.png';
import logoAhlstrom from '../../assets/ahlstrom_testimonial.png';
import { ReactComponent as ChevronLeftIcon } from '../../assets/chevron_left.svg';
import { ReactComponent as ChevronRightIcon } from '../../assets/chevron_right.svg';

const Testimonials = () => {
  const { t } = useTranslation();

  const testimonials = useMemo(() => ([
    {
      logo: logoJavaHuset,
      label: t('testimonials.item1.label'),
      background: t('testimonials.item1.background'),
      quote: t('testimonials.item1.quote'),
      author: t('testimonials.item1.author'),
    },
    {
      logo: logoSkeppsholms,
      label: t('testimonials.item2.label'),
      background: t('testimonials.item2.background'),
      quote: t('testimonials.item2.quote'),
      author: t('testimonials.item2.author'),
    },
    {
      logo: logoParamPara,
      label: t('testimonials.item3.label'),
      background: t('testimonials.item3.background'),
      quote: t('testimonials.item3.quote'),
      author: t('testimonials.item3.author'),
    },
    {
      logo: logoAhlstrom,
      label: t('testimonials.item4.label'),
      background: t('testimonials.item4.background'),
      quote: t('testimonials.item4.quote'),
      author: t('testimonials.item4.author'),
    }
  ]), [t]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const showControls = testimonials.length > 1;
  const intervalRef = useRef(null);
  const autoSlideDelayRef = useRef(9000); // Start with 9 seconds

  // Minimum swipe distance (in pixels) to trigger navigation
  const minSwipeDistance = 50;

  const startAutoSlide = useCallback((delay) => {
    if (!showControls) return;

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set new interval with specified delay
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, delay);

    autoSlideDelayRef.current = delay;
  }, [showControls, testimonials.length]);

  useEffect(() => {
    if (!showControls) {
      return undefined;
    }

    // Start with 9 seconds
    startAutoSlide(9000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [showControls, testimonials.length, startAutoSlide]);

  const goTo = (index) => {
    if (!showControls) return;
    setActiveIndex((index + testimonials.length) % testimonials.length);
    // User interacted, reset timer to 10 seconds
    startAutoSlide(10000);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !showControls) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goTo(activeIndex + 1);
    } else if (isRightSwipe) {
      goTo(activeIndex - 1);
    }
    
    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  const activeTestimonial = testimonials[activeIndex];

  return (
    <section className="testimonials" id="testimonials">
      <div className="testimonials-wrapper">
        <h2 className="testimonials-title">{t('testimonials.title')}</h2>

        <div className={`testimonial-carousel ${showControls ? '' : 'single'}`}>
          {showControls && (
            <button
              className="carousel-control prev"
              aria-label="Previous testimonial"
              onClick={() => goTo(activeIndex - 1)}
            >
              <ChevronLeftIcon className="carousel-chevron" />
            </button>
          )}
          <div 
            className="testimonial-slide"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="testimonial-logo">
              <img src={activeTestimonial.logo} alt="Customer logo" />
            </div>
            <div className="testimonial-content">
            
              <span className="testimonial-case-label">{activeTestimonial.label}</span>
              <p className="testimonial-background">{activeTestimonial.background}</p>
              <div className="quote-container">
                <span className="quote-symbol">"</span>
                <p className="testimonial-quote">{activeTestimonial.quote}</p>
              </div>
              <p className="testimonial-author">{activeTestimonial.author}</p>
            </div>
          </div>
          {showControls && (
            <button
              className="carousel-control next"
              aria-label="Next testimonial"
              onClick={() => goTo(activeIndex + 1)}
            >
              <ChevronRightIcon className="carousel-chevron" />
            </button>
          )}
        </div>

        {showControls && (
          <div className="testimonial-dots" role="tablist">
            {testimonials.map((_, index) => (
              <button
                key={`dot-${index}`}
                className={`dot ${index === activeIndex ? 'active' : ''}`}
                aria-label={`Show testimonial ${index + 1}`}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;

